import { api } from '../lib/api-client.js';

import type {
  AcknowledgeViolationInput,
  BulkAcknowledgeViolationsInput,
  CreateWorkOrderFromViolationInput,
  DismissViolationInput,
  LinkViolationToWorkOrderInput,
  MonitoredSourceResponse,
  MonitoredSourceToggleInput,
  ReopenViolationInput,
  SourceHealthResponse,
  ViolationAgency,
  ViolationAgencyBuildingsResponse,
  ViolationDashboardResponse,
  ViolationEventResponse,
  ViolationFiltersQuery,
  ViolationResponse,
  ViolationSourceId,
} from '@sitewise/shared';

/* ----------------------------- Types --------------------------------- */

export type Violation = ViolationResponse;
export type ViolationEvent = ViolationEventResponse;
export type MonitoredSource = MonitoredSourceResponse;
export type SourceHealth = SourceHealthResponse;
export type ViolationDashboard = ViolationDashboardResponse;
export type ViolationAgencyBuildings = ViolationAgencyBuildingsResponse;

export interface SourceCatalogEntry {
  id: ViolationSourceId;
  agency: string;
  displayName: string;
  defaultPollIntervalMs: number;
  isScraper: boolean;
  capabilities: {
    canLookupByBin: boolean;
    canLookupByBbl: boolean;
    canLookupByAddress: boolean;
  };
}

export interface LinkableWorkOrder {
  id: string;
  displayNumber: string;
  title: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  status: string;
  createdAt: string;
}

/* ----------------------------- Helpers ------------------------------- */

function qs(params: Record<string, unknown>): string {
  const search = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    if (Array.isArray(v)) {
      if (v.length > 0) search.set(k, v.join(','));
    } else {
      search.set(k, String(v));
    }
  }
  const s = search.toString();
  return s ? `?${s}` : '';
}

/* ----------------------------- List / detail ------------------------- */

export async function listViolations(filters: Partial<ViolationFiltersQuery> = {}) {
  return api<{ violations: Violation[]; nextCursor: string | null }>(
    `/violations${qs(filters as Record<string, unknown>)}`,
  );
}

export async function fetchViolation(id: string) {
  return api<{ violation: Violation; events: ViolationEvent[] }>(`/violations/${id}`);
}

export async function fetchLinkableWorkOrders(violationId: string) {
  return api<{ workOrders: LinkableWorkOrder[] }>(
    `/violations/${violationId}/linkable-work-orders`,
  );
}

export async function fetchViolationDashboard() {
  return api<ViolationDashboard>('/violations/dashboard');
}

export async function fetchAgencyBuildings(agency: ViolationAgency) {
  return api<ViolationAgencyBuildings>(`/violations/agencies/${agency}/buildings`);
}

export async function fetchSourceCatalog() {
  return api<{ sources: SourceCatalogEntry[] }>('/violations/sources/catalog');
}

/* ----------------------------- Mutations ----------------------------- */

export async function acknowledgeViolation(id: string, input: AcknowledgeViolationInput = {}) {
  return api<{ violation: Violation }>(`/violations/${id}/acknowledge`, {
    method: 'POST',
    body: input,
  });
}

export async function dismissViolation(id: string, input: DismissViolationInput) {
  return api<{ violation: Violation }>(`/violations/${id}/dismiss`, {
    method: 'POST',
    body: input,
  });
}

export async function reopenViolation(id: string, input: ReopenViolationInput = {}) {
  return api<{ violation: Violation }>(`/violations/${id}/reopen`, {
    method: 'POST',
    body: input,
  });
}

export async function linkViolationWorkOrder(
  id: string,
  input: LinkViolationToWorkOrderInput,
) {
  return api<{ violation: Violation }>(`/violations/${id}/link-work-order`, {
    method: 'POST',
    body: input,
  });
}

export async function unlinkViolationWorkOrder(id: string) {
  return api<{ violation: Violation }>(`/violations/${id}/unlink-work-order`, {
    method: 'POST',
  });
}

export async function createViolationWorkOrder(
  id: string,
  input: CreateWorkOrderFromViolationInput = {},
) {
  return api<{ workOrderId: string; displayNumber: string }>(
    `/violations/${id}/work-order`,
    { method: 'POST', body: input },
  );
}

export async function bulkAcknowledgeViolations(input: BulkAcknowledgeViolationsInput) {
  return api<{ acknowledged: number }>('/violations/bulk/acknowledge', {
    method: 'POST',
    body: input,
  });
}

export async function refreshViolation(id: string) {
  return api<{ enqueued: true }>(`/violations/${id}/refresh`, {
    method: 'POST',
    body: { force: true },
  });
}

/* ----------------------------- Building × source -------------------- */

export async function listBuildingSources(buildingId: string) {
  return api<{ sources: MonitoredSource[] }>(
    `/violations/buildings/${buildingId}/sources`,
  );
}

export async function toggleBuildingSource(
  buildingId: string,
  sourceId: ViolationSourceId,
  input: MonitoredSourceToggleInput,
) {
  return api<{ source: MonitoredSource }>(
    `/violations/buildings/${buildingId}/sources/${sourceId}`,
    { method: 'PUT', body: input },
  );
}

export async function refreshBuildingSource(
  buildingId: string,
  sourceId: ViolationSourceId,
) {
  return api<{ enqueued: true }>(
    `/violations/buildings/${buildingId}/sources/${sourceId}/refresh`,
    { method: 'POST', body: { force: true } },
  );
}

/* ----------------------------- Admin (super-admin only) ------------- */

export async function listSourceHealth() {
  return api<{ sources: SourceHealth[] }>('/admin/violations/sources/health');
}

export async function resetSourceHealth(sourceId: ViolationSourceId) {
  return api<{ ok: true }>(`/admin/violations/sources/${sourceId}/reset`, {
    method: 'POST',
  });
}
